// M15 Playlist – Editor (Entwurf mit automatischem Speichern), M19 Veröffentlichen-Dialog, Revisionen.
// Vorschau (M20) öffnet /preview/playlist/{id} mit der Player-Engine in einem neuen Tab.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { can, qk, useLayouts, useMe, usePlaylist, useRevisions, useTemplateCatalog } from '../api/queries';
import type { Fit, LayoutRow, Media, Playlist, PlaylistDetail, PlaylistItem, PlaylistRevision } from '../api/types';
import { PendingBanner, SubmitDialog, useApprovalRequired } from '../components/Approval';
import { Icon } from '../components/Icon';
import { ItemThumb, mediaUrl, useMediaIndex, type MediaIndex } from '../components/Preview';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Field, formatDateTime, formatDuration, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { openPreview } from '../preview/open';
import { DropZone, UploadList, useUploader } from './Media';
import { fillable } from './templates/shared';
import { EditCopyButton } from './template-edit/TemplateEdit';
import './template-edit/template-edit.css';

export interface DraftItem {
  key: string;
  id?: string;
  kind: 'media' | 'layout';
  mediaAssetId: string | null;
  layoutId: string | null;
  layoutContent: unknown;
  durationMs: number | null;
  fit: Fit;
  mute: boolean;
  validFrom: string | null;
  validUntil: string | null;
  enabled: boolean;
}

let keySeq = 0;
export const toDraft = (i: PlaylistItem): DraftItem => ({
  key: `k${++keySeq}`, id: i.id, kind: i.kind, mediaAssetId: i.mediaAssetId, layoutId: i.layoutId, layoutContent: i.layoutContent ?? null,
  durationMs: i.durationMs, fit: i.fit, mute: i.mute, validFrom: i.validFrom, validUntil: i.validUntil, enabled: i.enabled,
});
export const toInput = (i: DraftItem) => i.kind === 'media'
  ? { kind: 'media' as const, mediaAssetId: i.mediaAssetId!, durationMs: i.durationMs, fit: i.fit, mute: i.mute, validFrom: i.validFrom, validUntil: i.validUntil, enabled: i.enabled }
  : { kind: 'layout' as const, layoutId: i.layoutId!, layoutContent: (i.layoutContent ?? null) as Record<string, unknown> | null, durationMs: i.durationMs, validFrom: i.validFrom, validUntil: i.validUntil, enabled: i.enabled };

const newMediaItem = (m: Media): DraftItem => ({
  key: `k${++keySeq}`, kind: 'media', mediaAssetId: m.id, layoutId: null, layoutContent: null, durationMs: null, fit: 'contain', mute: true,
  validFrom: null, validUntil: null, enabled: true,
});
const newLayoutItem = (layoutId: string): DraftItem => ({
  key: `k${++keySeq}`, kind: 'layout', mediaAssetId: null, layoutId, layoutContent: {}, durationMs: null, fit: 'contain', mute: true,
  validFrom: null, validUntil: null, enabled: true,
});

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

type SaveState = 'saved' | 'dirty' | 'saving' | 'error';

export function PlaylistEditorPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: pl, isLoading } = usePlaylist(id);
  const media = useMediaIndex();
  const { data: layouts } = useLayouts();
  const { data: catalog } = useTemplateCatalog();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const manage = can(me, 'playlists.manage');
  // Freigabe-Workflow: einreichen statt veröffentlichen
  const approval = useApprovalRequired();

  const [items, setItems] = useState<DraftItem[] | null>(null);
  const [name, setName] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [state, setState] = useState<SaveState>('saved');
  const [dialog, setDialog] = useState<'library' | 'template' | 'publish' | 'revisions' | 'delete' | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const version = useRef<number>(0);
  const edits = useRef(0);
  const saving = useRef<Promise<void> | null>(null);
  const meta = useRef<PlaylistDetail | null>(null);
  const { rows: uploads, upload } = useUploader();

  useEffect(() => {
    if (pl && items === null) {
      setItems(pl.items.map(toDraft));
      setName(pl.name);
      version.current = pl.rowVersion;
      meta.current = pl;
    }
  }, [pl, items]);

  const cacheKey = [...qk.playlist(id), me?.activeTenantId ?? 'none'];
  const save = useCallback(async (list: DraftItem[]) => {
    const at = edits.current;
    setState('saving');
    try {
      const res = await api.put<PlaylistDetail>(`/playlists/${id}/items`, { items: list.map(toInput) }, { ifMatch: version.current });
      version.current = res.rowVersion;
      meta.current = res;
      qc.setQueryData(cacheKey, res);
      void qc.invalidateQueries({ queryKey: qk.playlists });
      if (edits.current === at) {
        setItems((cur) => cur && cur.map((x, i) => ({ ...x, id: res.items[i]?.id })));
        setState('saved');
      } else setState('dirty');
    } catch (e) {
      setState('error');
      toast.error(e);
    }
  }, [id, qc, toast]);

  // Automatisch speichern (Entwurf), entprellt
  useEffect(() => {
    if (state !== 'dirty' || !items) return;
    const h = setTimeout(() => {
      saving.current = (saving.current ?? Promise.resolve()).then(() => save(items));
    }, 700);
    return () => clearTimeout(h);
  }, [items, state, save]);

  const flush = async () => {
    if (state === 'dirty' && items) await save(items);
    await saving.current;
  };
  const update = (fn: (list: DraftItem[]) => DraftItem[]) => {
    edits.current++;
    setItems((cur) => (cur ? fn(cur) : cur));
    setState('dirty');
  };
  const patchItem = (key: string, p: Partial<DraftItem>) => update((l) => l.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const move = (key: string, d: number) => update((l) => {
    const i = l.findIndex((x) => x.key === key);
    const j = i + d;
    if (i < 0 || j < 0 || j >= l.length) return l;
    const n = [...l];
    [n[i], n[j]] = [n[j]!, n[i]!];
    return n;
  });
  const dropOn = (target: string) => {
    if (!drag || drag === target) return;
    update((l) => {
      const from = l.findIndex((x) => x.key === drag);
      const to = l.findIndex((x) => x.key === target);
      const n = [...l];
      const [it] = n.splice(from, 1);
      n.splice(to, 0, it!);
      return n;
    });
    setDrag(null);
  };

  const saveName = async () => {
    if (!pl || !name.trim() || name === meta.current?.name) return;
    try {
      await flush();
      const res = await api.patch<Playlist>(`/playlists/${id}`, { name: name.trim() }, { ifMatch: version.current });
      version.current = res.rowVersion;
      meta.current = { ...meta.current!, ...res };
      void qc.invalidateQueries({ queryKey: qk.playlists });
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
    }
  };

  const design = async (it: DraftItem) => {
    const l = layouts?.find((x) => x.id === it.layoutId);
    await flush();
    // Freie Folie ohne Felder: Studio; mit Feldern (Branchenvorlage): Formular M16
    if (l && (l.definition as { specVersion?: number }).specVersion === 2 && l.scope === 'tenant' && !fillable(l)) {
      navigate(`/studio/${l.id}?back=${encodeURIComponent(`/playlists/${id}`)}`);
      return;
    }
    const fresh = qc.getQueryData<PlaylistDetail>(cacheKey);
    const idx = items?.findIndex((x) => x.key === it.key) ?? -1;
    const itemId = fresh?.items[idx]?.id ?? it.id;
    if (itemId) navigate(`/playlists/${id}/items/${itemId}/design`);
  };

  const total = useMemo(() => (items ?? []).filter((i) => i.enabled).reduce((s, i) => {
    const m = i.mediaAssetId ? media.byId.get(i.mediaAssetId) : undefined;
    return s + (i.durationMs ?? (m?.kind === 'video' ? m.current?.durationMs ?? pl?.defaultDurationMs ?? 10000 : pl?.defaultDurationMs ?? 10000));
  }, 0), [items, media, pl?.defaultDurationMs]);

  if (isLoading || !pl || !items) return <Spinner />;
  const draftChanged = state !== 'saved' || (meta.current?.draftChanged ?? pl.draftChanged);
  const usedBy = meta.current?.usedBy ?? pl.usedBy;

  const addLayoutSlide = async (l: LayoutRow | 'new', orientation: 'landscape' | 'portrait' = 'landscape', studio = false) => {
    setDialog(null);
    try {
      // „Bearbeiten (eigene Kopie)": Kopie als Folie anhängen und im Studio öffnen
      if (l !== 'new' && studio) {
        const list = [...items, newLayoutItem(l.id)];
        edits.current++;
        setItems(list);
        await save(list);
        navigate(`/studio/${l.id}?back=${encodeURIComponent(`/playlists/${id}`)}`);
        return;
      }
      if (l === 'new') {
        const created = await api.post<LayoutRow>('/layouts', { name: `${name} – ${t('playlists.slide')} ${items.length + 1}`, orientation });
        await qc.invalidateQueries({ queryKey: qk.layouts });
        const list = [...items, newLayoutItem(created.id)];
        edits.current++;
        setItems(list);
        await save(list);
        navigate(`/studio/${created.id}?back=${encodeURIComponent(`/playlists/${id}`)}`);
        return;
      }
      // Branchenvorlage: eigene Kopie anlegen (wie „Vorlage verwenden“ in der Galerie)
      const industry = catalog?.items.find((x) => x.layoutId === l.id);
      let use = l;
      if (industry) {
        use = (await api.post<{ layout: LayoutRow }>(`/templates/${industry.key}/use`, {})).layout;
        await qc.invalidateQueries({ queryKey: qk.layouts });
      }
      const list = [...items, newLayoutItem(use.id)];
      edits.current++;
      setItems(list);
      await save(list);
      const fresh = qc.getQueryData<PlaylistDetail>(cacheKey);
      const last = fresh?.items[fresh.items.length - 1];
      if (fillable(use) && last) navigate(`/playlists/${id}/items/${last.id}/design`);
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <>
      <div className="pl-head" style={{ marginBottom: 6 }}>
        <Link className="btn ghost icon" to="/playlists" aria-label={t('common.back')}><Icon name="left" /></Link>
        {manage ? <input className="title" aria-label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} /> : <h1 className="grow">{pl.name}</h1>}
        <span className="savestate" role="status" aria-live="polite">{t(`playlists.save.${state}`)}</span>
        <button className="btn" onClick={() => void openPreview('playlist', id, flush).catch(toast.error)} title={t('preview.openHint')}><Icon name="play" />{t('playlists.preview')}</button>
        <button className="btn" onClick={() => setDialog('revisions')}><Icon name="clock" />{t('playlists.revisions')}</button>
        {can(me, 'playlists.publish') && (
          <button className="btn primary" onClick={async () => { await flush(); setDialog('publish'); }} disabled={!items.length}>
            <Icon name="send" />{approval ? t('approval.submit.button') : t('playlists.publish')}{draftChanged ? ' ●' : ''}
          </button>
        )}
      </div>
      <PendingBanner type="playlist" id={id} />
      <div className="muted small" style={{ marginBottom: 8 }}>
        {pl.latestRevisionNo ? t('playlists.latest', { no: pl.latestRevisionNo }) : t('playlists.neverPublished')}
        {' · '}{t('playlists.total', { d: formatDuration(total) })}{' · '}{t('playlists.shownOn', { a: usedBy.assignments, s: usedBy.schedules })}
      </div>

      <div className="items" onDragOver={(e) => e.preventDefault()}>
        {items.length === 0 && <div className="hint">{t('playlists.noItems')}</div>}
        {items.map((it, i) => {
          const m = it.mediaAssetId ? media.byId.get(it.mediaAssetId) : undefined;
          const l = it.layoutId ? layouts?.find((x) => x.id === it.layoutId) : undefined;
          const title = it.kind === 'media' ? (m?.name ?? t('media.missing')) : (l?.name ?? t('playlists.slide'));
          const isVideo = m?.kind === 'video';
          const notReady = it.kind === 'media' && m?.current?.status !== 'ready';
          return (
            <div key={it.key} className={`item${sel === it.key ? ' sel' : ''}${drag === it.key ? ' dragging' : ''}${it.enabled ? '' : ' off'}`}
              draggable={manage} onDragStart={() => setDrag(it.key)} onDragEnd={() => setDrag(null)} onDrop={() => dropOn(it.key)}
              onClick={(e) => !(e.target as HTMLElement).closest('button,input,select') && setSel(sel === it.key ? null : it.key)}
              data-testid="playlist-item">
              <div className="num" title={t('playlists.dragHint')}>{i + 1}</div>
              <div className="thumb"><ItemThumb item={it} media={media} layouts={layouts} /></div>
              <div className="meta">
                <b title={title}>{title}</b>
                <div className="row small muted" style={{ gap: 8 }}>
                  <span>{it.kind === 'media' ? (isVideo ? t('media.video') : t('media.image')) : t('playlists.slide')}</span>
                  <span><Icon name="clock" /> {it.durationMs ? formatDuration(it.durationMs) : isVideo ? t('playlists.videoLength') : formatDuration(pl.defaultDurationMs)}</span>
                  {!it.enabled && <span className="pill neutral plain">{t('playlists.disabled')}</span>}
                  {notReady && <span className="pill warn plain">{t('playlists.notReady')}</span>}
                  {(it.validFrom || it.validUntil) && <span className="pill info plain">{t('playlists.timed')}</span>}
                </div>
              </div>
              {manage && (
                <div className="acts">
                  {it.kind === 'layout' && <button className="btn sm primary" onClick={() => void design(it)}><Icon name="edit" />{t('playlists.design')}</button>}
                  <button className="btn sm ghost icon" title={t('playlists.up')} aria-label={t('playlists.up')} onClick={() => move(it.key, -1)}><Icon name="up" /></button>
                  <button className="btn sm ghost icon" title={t('playlists.down')} aria-label={t('playlists.down')} onClick={() => move(it.key, 1)}><Icon name="down" /></button>
                  <button className="btn sm ghost icon" title={t('playlists.duplicate')} aria-label={t('playlists.duplicate')}
                    onClick={() => update((list) => { const n = [...list]; n.splice(i + 1, 0, { ...structuredClone(it), key: `k${++keySeq}`, id: undefined }); return n; })}><Icon name="copy" /></button>
                  <button className="btn sm ghost icon danger" title={t('common.remove')} aria-label={t('common.remove')} onClick={() => update((list) => list.filter((x) => x.key !== it.key))}><Icon name="trash" /></button>
                </div>
              )}
              {sel === it.key && manage && (
                <div className="editor">
                  <Field label={t('playlists.durationS')}>
                    <input type="number" min={1} placeholder={isVideo ? t('playlists.videoLength') : String(pl.defaultDurationMs / 1000)}
                      value={it.durationMs ? it.durationMs / 1000 : ''} onChange={(e) => patchItem(it.key, { durationMs: e.target.value ? Math.max(1, Number(e.target.value)) * 1000 : null })} />
                  </Field>
                  {it.kind === 'media' && (
                    <Field label={t('playlists.fit')}>
                      <select value={it.fit} onChange={(e) => patchItem(it.key, { fit: e.target.value as Fit })}>
                        <option value="contain">{t('fit.contain')}</option><option value="cover">{t('fit.cover')}</option><option value="stretch">{t('fit.stretch')}</option>
                      </select>
                    </Field>
                  )}
                  <Field label={t('playlists.validFrom')}><input type="datetime-local" value={toLocalInput(it.validFrom)} onChange={(e) => patchItem(it.key, { validFrom: fromLocalInput(e.target.value) })} /></Field>
                  <Field label={t('playlists.validUntil')}><input type="datetime-local" value={toLocalInput(it.validUntil)} onChange={(e) => patchItem(it.key, { validUntil: fromLocalInput(e.target.value) })} /></Field>
                  <div className="field">{t('playlists.options')}
                    <label className="check"><input type="checkbox" checked={it.enabled} onChange={(e) => patchItem(it.key, { enabled: e.target.checked })} />{t('playlists.active')}</label>
                    {isVideo && <label className="check"><input type="checkbox" checked={!it.mute} onChange={(e) => patchItem(it.key, { mute: !e.target.checked })} />{t('playlists.sound')}</label>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {manage && (
        <>
          <div className="add-bar">
            <button className="btn primary" onClick={() => setDialog('library')}><Icon name="image" />{t('playlists.fromLibrary')}</button>
            <button className="btn" onClick={() => setDialog('template')}><Icon name="layout" />{t('playlists.fromTemplate')}</button>
            <button className="btn" onClick={() => void addLayoutSlide('new')}><Icon name="edit" />{t('playlists.newDesigner')}</button>
            <span className="grow" />
            <button className="btn ghost danger" onClick={() => setDialog('delete')}><Icon name="trash" />{t('playlists.delete')}</button>
          </div>
          <div style={{ marginTop: 12 }}>
            <DropZone compact onFiles={async (files) => {
              const ids = await upload(files);
              await qc.refetchQueries({ queryKey: qk.media });
              const all = qc.getQueriesData<Media[]>({ queryKey: qk.media }).flatMap(([, d]) => d ?? []);
              const added = ids.flatMap((x) => { const m = all.find((y) => y.id === x); return m ? [newMediaItem(m)] : []; });
              if (added.length) update((l) => [...l, ...added]);
            }} />
            <UploadList rows={uploads} />
          </div>
        </>
      )}

      {dialog === 'library' && <LibraryDialog media={media} onClose={() => setDialog(null)} onPick={(ms) => { update((l) => [...l, ...ms.map(newMediaItem)]); setDialog(null); }} />}
      {dialog === 'template' && <TemplatePickDialog layouts={layouts ?? []} media={media} onClose={() => setDialog(null)} onPick={(l) => void addLayoutSlide(l)}
        onEdit={(copy) => addLayoutSlide(copy, copy.orientation, true)} />}
      {dialog === 'publish' && <PublishDialog playlist={meta.current ?? pl} items={items} media={media} layouts={layouts ?? []} approval={approval} onClose={() => setDialog(null)}
        onPublished={(res) => { version.current = res.rowVersion; meta.current = res; qc.setQueryData(cacheKey, res); }} />}
      {dialog === 'revisions' && <RevisionsDialog playlistId={id} canRestore={manage} onClose={() => setDialog(null)} onRestore={(list) => { update(() => list); setDialog(null); toast.show(t('playlists.restored')); }} />}
      {dialog === 'delete' && (
        <Confirm danger title={t('playlists.delete')} text={t('playlists.confirmDelete', { name: pl.name })} confirmLabel={t('common.delete')} onClose={() => setDialog(null)}
          onConfirm={async () => {
            try {
              await api.del(`/playlists/${id}`, { ifMatch: version.current });
              await qc.invalidateQueries({ queryKey: qk.playlists });
              navigate('/playlists');
            } catch (e) {
              toast.error(e);
              setDialog(null);
            }
          }} />
      )}
      <div className="muted small" style={{ marginTop: 16 }}>{t('playlists.changedAt', { when: formatDateTime((meta.current ?? pl).updatedAt, locale) })}</div>
    </>
  );
}

function LibraryDialog({ media, onClose, onPick }: { media: MediaIndex; onClose: () => void; onPick: (m: Media[]) => void }) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const list = media.list.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog wide title={t('playlists.fromLibrary')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!sel.length} onClick={() => onPick(sel.flatMap((id) => media.byId.get(id) ?? []))}>{t('playlists.addCount', { count: sel.length })}</button>
    </>}>
      <input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
      {list.length ? (
        <div className="media-grid" style={{ marginTop: 0 }}>
          {list.map((m) => (
            <figure key={m.id} className={sel.includes(m.id) ? 'sel' : ''} role="checkbox" aria-checked={sel.includes(m.id)} tabIndex={0} style={{ cursor: 'pointer' }}
              onClick={() => setSel((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]))}
              onKeyDown={(e) => e.key === ' ' && setSel((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]))}>
              {m.kind === 'image' ? <img src={mediaUrl(m)} alt="" loading="lazy" /> : <video src={`${mediaUrl(m)}#t=1`} muted preload="metadata" />}
              <figcaption><span className="n">{m.name}</span></figcaption>
            </figure>
          ))}
        </div>
      ) : <div className="muted">{t('media.empty')}</div>}
    </Dialog>
  );
}

export function TemplatePickDialog({ layouts, media, onClose, onPick, onEdit }: {
  layouts: LayoutRow[]; media: MediaIndex; onClose: () => void; onPick: (l: LayoutRow) => void;
  /** „Bearbeiten (eigene Kopie)" bei schreibgeschützten Vorlagen: erhält die Kopie. */
  onEdit?: (copy: LayoutRow) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [o, setO] = useState<'' | 'landscape' | 'portrait'>('');
  const usable = layouts.filter((l) => (l.latestRevisionNo !== null || l.scope === 'tenant') && (!o || l.orientation === o)
    && (!q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <Dialog wide title={t('playlists.fromTemplate')} onClose={onClose} actions={<button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>}>
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('gallery.search')} aria-label={t('gallery.search')} />
        <div className="seg" role="group" aria-label={t('templates.format')}>
          {(['', 'landscape', 'portrait'] as const).map((x) => (
            <button key={x || 'all'} className={o === x ? 'on' : ''} aria-pressed={o === x} onClick={() => setO(x)}>
              {x === '' ? t('common.all') : x === 'landscape' ? '16:9' : '9:16'}
            </button>
          ))}
        </div>
      </div>
      <div className="grid-cards" style={{ maxHeight: '60vh', overflow: 'auto' }}>
        {usable.map((l) => (
          <div key={l.id} className="card te-pick" data-testid="pick-card">
            <button className="te-pick-main" onClick={() => onPick(l)}>
              <TemplatePreview layout={l} media={media} />
              <b>{l.name}</b>
              <span className="muted small">{l.scope === 'global' ? t('templates.global') : t('templates.own')} · {l.orientation === 'portrait' ? '9:16' : '16:9'}</span>
            </button>
            {onEdit && (l.scope === 'global' || (l.definition as { specVersion?: number }).specVersion === 1) && <div><EditCopyButton layout={l} small onCopied={onEdit} /></div>}
          </div>
        ))}
      </div>
    </Dialog>
  );
}

export function TemplatePreview({ layout, media, content }: { layout: LayoutRow; media: MediaIndex; content?: unknown }) {
  const portrait = layout.orientation === 'portrait';
  return (
    <div className="prev landscape" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ position: 'relative', height: '100%', aspectRatio: portrait ? '9/16' : '16/9' }}>
        <ItemThumb item={{ kind: 'layout', layoutId: layout.id, mediaAssetId: null, layoutContent: content ?? null }} media={media} layouts={[layout]} />
      </div>
    </div>
  );
}

/** M19 Veröffentlichen: Änderungen gegenüber der letzten Revision, Warnungen, betroffene Bildschirme. */
function PublishDialog({ playlist, items, media, layouts, approval, onClose, onPublished }: {
  playlist: PlaylistDetail; items: DraftItem[]; media: MediaIndex; layouts: LayoutRow[]; approval?: boolean; onClose: () => void; onPublished: (p: PlaylistDetail) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ revisionNo: number; affectedDevices: number } | null>(null);
  const [prev, setPrev] = useState<{ items: { kind: string; mediaAssetId?: string; layoutId?: string; durationMs?: number }[] } | null>(null);
  useEffect(() => {
    if (playlist.latestRevisionNo) {
      void api.get<PlaylistRevision>(`/playlists/${playlist.id}/revisions/${playlist.latestRevisionNo}`).then((r) => setPrev(r.content as typeof prev));
    }
  }, [playlist.id, playlist.latestRevisionNo]);
  const enabled = items.filter((i) => i.enabled);
  const sig = (x: { kind: string; mediaAssetId?: string | null; layoutId?: string | null }) => `${x.kind}:${x.mediaAssetId ?? x.layoutId}`;
  const before = (prev?.items ?? []).map(sig);
  const now = enabled.map(sig);
  const added = now.filter((s) => !before.includes(s)).length;
  const removed = before.filter((s) => !now.includes(s)).length;
  const blocking = enabled.filter((i) => i.kind === 'media' && media.byId.get(i.mediaAssetId ?? '')?.current?.status !== 'ready');
  const unpublishedLayouts = enabled.filter((i) => i.kind === 'layout' && layouts.find((l) => l.id === i.layoutId)?.latestRevisionNo === null);
  // Freigabe an: derselbe Überblick, aber Einreichen mit Notiz statt Veröffentlichen
  if (approval) {
    return (
      <SubmitDialog type="playlist" id={playlist.id} name={playlist.name} onClose={onClose}>
        <dl className="props">
          <dt>{t('publish.items')}</dt><dd>{enabled.length}</dd>
          {playlist.latestRevisionNo ? <><dt>{t('publish.changes')}</dt><dd>{t('publish.diff', { added, removed, rev: playlist.latestRevisionNo })}</dd></> : null}
          <dt>{t('publish.screens')}</dt><dd>{t('playlists.shownOn', { a: playlist.usedBy.assignments, s: playlist.usedBy.schedules })}</dd>
        </dl>
        {blocking.length > 0 && <div className="hint warn">{t('publish.blocking', { count: blocking.length })}</div>}
        {unpublishedLayouts.length > 0 && <div className="hint warn">{t('publish.layoutsUnpublished')}</div>}
      </SubmitDialog>
    );
  }

  const publish = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ revisionNo: number; affectedDevices: number }>(`/playlists/${playlist.id}/publish`);
      setResult(res);
      const fresh = await api.get<PlaylistDetail>(`/playlists/${playlist.id}`);
      onPublished(fresh);
      await Promise.all([qc.invalidateQueries({ queryKey: qk.playlists }), qc.invalidateQueries({ queryKey: qk.dashboard }), qc.invalidateQueries({ queryKey: qk.revisions(playlist.id) })]);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t('publish.title', { name: playlist.name })} onClose={onClose} actions={result ? <button className="btn primary" onClick={onClose}>{t('common.close')}</button> : <>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={busy || blocking.length > 0 || unpublishedLayouts.length > 0} onClick={publish}><Icon name="send" />{t('publish.now')}</button>
    </>}>
      {result ? (
        <div className="hint" style={{ background: 'var(--ok-soft)', color: 'var(--ok)', border: 0 }} role="status">
          {t('publish.done', { no: result.revisionNo, count: result.affectedDevices })}
        </div>
      ) : (
        <>
          <dl className="props">
            <dt>{t('publish.items')}</dt><dd>{enabled.length}</dd>
            {playlist.latestRevisionNo ? <><dt>{t('publish.changes')}</dt><dd>{t('publish.diff', { added, removed, rev: playlist.latestRevisionNo })}</dd></> : null}
            <dt>{t('publish.screens')}</dt><dd>{t('playlists.shownOn', { a: playlist.usedBy.assignments, s: playlist.usedBy.schedules })}</dd>
          </dl>
          {blocking.length > 0 && <div className="hint warn">{t('publish.blocking', { count: blocking.length })}</div>}
          {unpublishedLayouts.length > 0 && <div className="hint warn">{t('publish.layoutsUnpublished')}</div>}
        </>
      )}
    </Dialog>
  );
}

interface RevItem { kind: 'media' | 'layout'; mediaAssetId?: string; layoutId?: string; layoutContent?: unknown; durationMs?: number | null; fit?: Fit; mute?: boolean; validFrom?: string | null; validUntil?: string | null }

function RevisionsDialog({ playlistId, canRestore, onClose, onRestore }: { playlistId: string; canRestore: boolean; onClose: () => void; onRestore: (items: DraftItem[]) => void }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: revs, isLoading } = useRevisions(playlistId);
  const toast = useToast();
  const restore = async (no: number) => {
    try {
      const r = await api.get<PlaylistRevision>(`/playlists/${playlistId}/revisions/${no}`);
      const items = ((r.content as { items?: RevItem[] })?.items ?? []).map((x): DraftItem => ({
        key: `k${++keySeq}`, kind: x.kind, mediaAssetId: x.mediaAssetId ?? null, layoutId: x.layoutId ?? null, layoutContent: x.layoutContent ?? null,
        durationMs: x.durationMs ?? null, fit: x.fit ?? 'contain', mute: x.mute ?? true, validFrom: x.validFrom ?? null, validUntil: x.validUntil ?? null, enabled: true,
      }));
      onRestore(items);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('playlists.revisions')} onClose={onClose} actions={<button className="btn" onClick={onClose}>{t('common.close')}</button>}>
      {isLoading ? <Spinner /> : revs?.length ? (
        <ul className="list-plain">
          {revs.map((r) => (
            <li key={r.id}>
              <b>{t('playlists.revision', { no: r.revisionNo })}</b>
              <span className="grow muted small">{formatDateTime(r.publishedAt, locale)}</span>
              {canRestore && <button className="btn sm" onClick={() => void restore(r.revisionNo)}>{t('playlists.restore')}</button>}
            </li>
          ))}
        </ul>
      ) : <div className="muted">{t('playlists.noRevisions')}</div>}
    </Dialog>
  );
}
